import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNowStrict } from "date-fns";
import { clsx } from "clsx";
import { api } from "../lib/api";
import type { NotificationItem } from "../lib/types";
import { NotificationDetailModal } from "./NotificationBell";

// Dashboard-sized notifications feed. Pulls from the same inbox
// the bell popover uses, but renders a compact list (5 most
// recent) inside the dashboard widget frame. Clicking a row
// opens the full NotificationDetailModal and marks the row read.
// A Mark-all-read action sits in the header when there are unread
// rows; a "View all" link in the footer scrolls up to the bell.
export function NotificationsWidget() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["notifications"],
    queryFn: api.listNotifications,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const markOne = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const markAll = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const items = list.data ?? [];
  const unread = items.filter((n) => !n.readAt).length;
  // 5 most recent for the widget — anything older stays accessible
  // via the bell popover. list is already server-ordered newest
  // first, so slice is enough.
  const shown = items.slice(0, 5);
  const selected = selectedId
    ? items.find((n) => n.id === selectedId) ?? null
    : null;

  return (
    // Match the chrome every other dashboard widget uses — white
    // card with slate border, rounded corners — so the widget pool
    // reads as one visual family. overflow-hidden keeps the inner
    // divider lines flush with the rounded corners.
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
      <div className="px-3 py-2 flex items-center justify-between border-b border-slate-100 dark:border-slate-700">
        <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">
          {unread > 0
            ? `${unread} unread notification${unread === 1 ? "" : "s"}`
            : "All caught up"}
        </div>
        {unread > 0 && (
          <button
            type="button"
            onClick={() => markAll.mutate()}
            disabled={markAll.isPending}
            className="text-[11px] text-sky-700 dark:text-sky-300 hover:underline disabled:opacity-50"
          >
            Mark all read
          </button>
        )}
      </div>

      {list.isLoading ? (
        <div className="px-4 py-6 text-center text-xs text-slate-500 dark:text-slate-400">
          Loading…
        </div>
      ) : shown.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <div className="text-2xl text-slate-300 dark:text-slate-600">⊘</div>
          <div className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
            No notifications yet.
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-700">
          {shown.map((n) => (
            <WidgetRow
              key={n.id}
              item={n}
              onOpen={() => {
                if (!n.readAt) markOne.mutate(n.id);
                setSelectedId(n.id);
              }}
            />
          ))}
        </ul>
      )}

      {items.length > shown.length && (
        <div className="px-3 py-2 border-t border-slate-100 dark:border-slate-700 text-right">
          <span className="text-[11px] text-slate-500 dark:text-slate-400">
            +{items.length - shown.length} older — open the bell to see all
          </span>
        </div>
      )}

      {selected && (
        <NotificationDetailModal
          item={selected}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

function WidgetRow({
  item, onOpen,
}: {
  item: NotificationItem;
  onOpen: () => void;
}) {
  const unread = !item.readAt;
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/60"
      >
        <div className="flex items-start gap-2">
          <span
            className={clsx(
              "mt-1.5 w-2 h-2 rounded-full shrink-0",
              unread ? "bg-sky-500" : "bg-transparent",
            )}
            aria-label={unread ? "unread" : undefined}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <div
                className={clsx(
                  "text-xs truncate",
                  unread
                    ? "font-semibold text-slate-900 dark:text-slate-50"
                    : "text-slate-700 dark:text-slate-200",
                )}
              >
                {item.subject}
              </div>
              <div className="text-[10px] text-slate-400 dark:text-slate-500 whitespace-nowrap shrink-0">
                {timeAgoShort(item.createdAt)}
              </div>
            </div>
            {item.body && (
              <div className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400 line-clamp-1 leading-snug">
                {item.body}
              </div>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

function timeAgoShort(iso: string): string {
  try {
    return formatDistanceToNowStrict(new Date(iso), { addSuffix: false });
  } catch {
    return "";
  }
}
